import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { Modal } from "../../common-components/modal";
import Typography from "../../common-components/Typography";
import Action from "../../common-components/Action";
import CheckBox from "../../common-components/CheckBox";
import { font } from "../../constants/font";
import content from "../../constants/content";
import vmTheme from "../../constants/theme";

export const DISLIKE_REASONS = [
  { id: "weak_hook", labelKey: content.SHORTS_DISLIKE_WEAK_HOOK },
  { id: "weak_story", labelKey: content.SHORTS_DISLIKE_WEAK_STORY },
  { id: "incomplete", labelKey: content.SHORTS_DISLIKE_INCOMPLETE },
  { id: "wrong_start", labelKey: content.SHORTS_DISLIKE_WRONG_START },
  { id: "caption_mistakes", labelKey: content.SHORTS_DISLIKE_CAPTION_MISTAKES },
  { id: "poor_framing", labelKey: content.SHORTS_DISLIKE_POOR_FRAMING },
  { id: "misses_moment", labelKey: content.SHORTS_DISLIKE_MISSES_MOMENT },
  { id: "too_short", labelKey: content.SHORTS_DISLIKE_TOO_SHORT },
  { id: "too_long", labelKey: content.SHORTS_DISLIKE_TOO_LONG },
  { id: "other", labelKey: content.SHORTS_DISLIKE_OTHER },
];

const ERROR_COLOR = "#E53935";

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 600px;
  max-height: 75vh;
  box-sizing: border-box;
  background: ${(props) => props.theme.polarColor};
  border-radius: 12px;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  flex-shrink: 0;
  padding: 24px 28px 16px;
  background: ${(props) => props.theme.polarColor};
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 28px;
  -webkit-overflow-scrolling: touch;
`;

const ReasonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ReasonRow = styled.div`
  border: 1px solid
    ${(props) => (props.$selected ? "#00BAFF" : props.theme.checkboxBorderColor)};
  border-radius: 12px;
  padding: 14px 16px;
  cursor: pointer;
  background: ${(props) => (props.$selected ? "#E7F8FF" : props.theme.polarColor)};
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover {
    border-color: #00BAFF;
  }
`;

const CheckBoxWrap = styled.div`
  pointer-events: none;
  width: 100%;

  label {
    color: ${(props) => props.theme.panelPrimaryColor};
    font: ${font.normalBase_21};
    flex: 1;
    line-height: 1.4;
  }
`;

const OtherTextarea = styled.textarea`
  width: 100%;
  min-height: 88px;
  margin-top: 12px;
  padding: 12px;
  border: 1px solid
    ${(props) => (props.$hasError ? ERROR_COLOR : props.theme.checkboxBorderColor)};
  border-radius: 8px;
  background: ${(props) => props.theme.polarColor};
  color: ${(props) => props.theme.panelPrimaryColor};
  font: ${font.normalBase_21};
  resize: vertical;
  box-sizing: border-box;
  pointer-events: auto;

  &::placeholder {
    color: ${(props) => props.theme.sbSecondaryColor};
  }

  &:focus {
    outline: none;
    border-color: ${(props) => (props.$hasError ? ERROR_COLOR : props.theme.checkBoxTickColor)};
  }
`;

const InlineError = styled.span`
  display: block;
  margin-top: 6px;
  font: ${font.normal_14};
  color: ${ERROR_COLOR};
`;

const SelectionError = styled.div`
  margin-bottom: 10px;
  font: ${font.normal_14};
  color: ${ERROR_COLOR};
`;

const ModalFooter = styled.div`
  flex-shrink: 0;
  padding: 16px 28px 24px;
  background: #FFF;
  border-radius: 0 0 16px 16px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const ShortsDislikeModal = ({ showModal, onClose, onSubmit, isSubmitting }) => {
  const intl = useIntl();
  const theme = useSelector((state) => state.app.get("theme"));
  const t = vmTheme[theme];
  const [selectedReasons, setSelectedReasons] = useState({});
  const [otherText, setOtherText] = useState("");
  const [selectionError, setSelectionError] = useState("");
  const [otherError, setOtherError] = useState("");
  const scrollAreaRef = useRef(null);
  const otherRowRef = useRef(null);

  useEffect(() => {
    if (!showModal) {
      setSelectedReasons({});
      setOtherText("");
      setSelectionError("");
      setOtherError("");
    }
  }, [showModal]);

  const scrollOtherIntoView = useCallback(() => {
    requestAnimationFrame(() => {
      if (!otherRowRef.current || !scrollAreaRef.current) return;
      const scrollArea = scrollAreaRef.current;
      const otherRow = otherRowRef.current;
      scrollArea.scrollTo({
        top: otherRow.offsetTop - scrollArea.offsetTop,
        behavior: "smooth",
      });
    });
  }, []);

  const toggleReason = useCallback((reasonId) => {
    setSelectionError("");
    if (reasonId === "other") {
      setOtherError("");
    }
    setSelectedReasons((prev) => {
      const nextChecked = !prev[reasonId];
      const next = { ...prev, [reasonId]: nextChecked };
      if (reasonId === "other" && nextChecked) {
        scrollOtherIntoView();
      }
      return next;
    });
  }, [scrollOtherIntoView]);

  const handleOtherChange = useCallback((e) => {
    setOtherText(e.target.value);
    if (e.target.value.trim()) {
      setOtherError("");
    }
  }, []);

  const handleSubmit = useCallback(() => {
    const hasSelection = Object.values(selectedReasons).some(Boolean);
    let hasError = false;

    if (!hasSelection) {
      setSelectionError(intl.formatMessage({ id: content.SHORTS_DISLIKE_SELECT_REASON }));
      hasError = true;
    } else {
      setSelectionError("");
    }

    if (selectedReasons.other && !otherText.trim()) {
      setOtherError(intl.formatMessage({ id: content.SHORTS_DISLIKE_OTHER_REQUIRED }));
      scrollOtherIntoView();
      hasError = true;
    } else {
      setOtherError("");
    }

    if (hasError) return;

    const selectedLabels = DISLIKE_REASONS
      .filter((reason) => selectedReasons[reason.id] && reason.id !== "other")
      .map((reason) => intl.formatMessage({ id: reason.labelKey }));

    if (selectedReasons.other) {
      selectedLabels.push(`Other: ${otherText.trim()}`);
    }

    onSubmit(selectedLabels.join(", "));
  }, [intl, onSubmit, otherText, scrollOtherIntoView, selectedReasons]);

  return (
    <Modal
      width="512px"
      showModal={showModal}
      onClose={onClose}
      showBackdrop
      height="auto"
      maxHeight="90vh"
      overflow="hidden"
      borderRadius="12px"
      padding="0"
      zIndex={9900}
    >
      <ModalBody>
        <ModalHeader>
          <Typography
            content={content.SHORTS_DISLIKE_TITLE}
            font={font.bold_16}
            color={t.panelPrimaryColor}
            enableTrim={false}
            padding="0"
            display="block"
          />
          {/* <Typography
            content={content.SHORTS_DISLIKE_SUBTITLE}
            font={font.normal_14}
            color={t.sbSecondaryColor}
            padding="8px 0 0"
            display="block"
            enableTrim={false}
          /> */}
        </ModalHeader>

        <ScrollArea ref={scrollAreaRef}>
          {selectionError && <SelectionError>{selectionError}</SelectionError>}
          <ReasonList>
            {DISLIKE_REASONS.map((reason) => {
              const isSelected = !!selectedReasons[reason.id];
              return (
                <ReasonRow
                  key={reason.id}
                  ref={reason.id === "other" ? otherRowRef : null}
                  $selected={isSelected}
                  onClick={() => toggleReason(reason.id)}
                >
                  <CheckBoxWrap>
                    <CheckBox
                      id={`dislike-reason-${reason.id}`}
                      label={intl.formatMessage({ id: reason.labelKey })}
                      isChecked={isSelected}
                      onHandlerClick={() => {}}
                      checkBoxColor={t.checkboxBorderColor}
                      checkBoxActiveColor={t.checkBoxTickColor}
                      checkBoxWidth="18px"
                      checkBoxHeight="18px"
                      borderRadius="4px"
                    />
                  </CheckBoxWrap>
                  {reason.id === "other" && isSelected && (
                    <>
                      <OtherTextarea
                        placeholder={intl.formatMessage({ id: content.SHORTS_DISLIKE_OTHER_PLACEHOLDER })}
                        value={otherText}
                        $hasError={!!otherError}
                        onChange={handleOtherChange}
                        onClick={(e) => e.stopPropagation()}
                      />
                      {otherError && <InlineError>{otherError}</InlineError>}
                    </>
                  )}
                </ReasonRow>
              );
            })}
          </ReasonList>
        </ScrollArea>

        <ModalFooter>
          <Action
            text={content.CANCEL}
            width="138px"
            height="40px"
            font={font.mediumBase_21}
            fontColor={t.panelPrimaryColor}
            background="#FFF"
            borderColor="#D5D7E0"
            borderWidth="1px"
            borderRadius="8px"
            marginTop="0"
            marginBottom="0"
            showHoverColor={false}
            isDisabled={isSubmitting}
            onClick={onClose}
          />
          <Action
            text={content.SUBMIT}
            width="138px"
            height="40px"
            font={font.mediumBase_21}
            fontColor={t.polarColor}
            background="#00BAFF"
            borderColor="#00BAFF"
            borderWidth="1px"
            borderRadius="8px"
            marginTop="0"
            marginBottom="0"
            showHoverColor={false}
            isDisabled={isSubmitting}
            isLoading={isSubmitting}
            onClick={handleSubmit}
          />
        </ModalFooter>
      </ModalBody>
    </Modal>
  );
};

ShortsDislikeModal.propTypes = {
  showModal: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  isSubmitting: PropTypes.bool,
};

export default ShortsDislikeModal;
